import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CopyButton } from '@/components/ui/CopyButton';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs } from '@/components/ui/tabs';
import { AccountsPanel } from '@/components/personas/AccountsPanel';
import { FieldCard } from '@/components/personas/FieldCard';
import { ListCard } from '@/components/personas/ListCard';
import { PersonaBriefPanel } from '@/components/personas/PersonaBriefPanel';
import { ResearchPanel } from '@/components/personas/ResearchPanel';
import { api } from '@/lib/api';
import { archetypeLabel, formatRelative, shortId } from '@/lib/format';

type TabKey = 'voice' | 'brief' | 'gallery' | 'content' | 'accounts' | 'research';

export function PersonaDetailPage({ personaId }: { personaId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>('voice');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);

  const personaQ = useQuery({
    queryKey: ['persona', personaId],
    queryFn: () => api.getPersona(personaId),
  });
  const contentQ = useQuery({
    queryKey: ['persona', personaId, 'content'],
    queryFn: () => api.listPersonaContent(personaId),
    enabled: tab === 'content',
  });
  const accountsQ = useQuery({
    queryKey: ['persona', personaId, 'accounts'],
    queryFn: () => api.listPersonaAccounts(personaId),
    enabled: tab === 'accounts',
  });

  const deleteM = useMutation({
    mutationFn: async () => {
      await api.deletePersona(personaId);
    },
    onSuccess: () => {
      toast.success('Persona moved to Trash', {
        description: 'Restore it from the Trash view on the Personas list.',
      });
      void queryClient.invalidateQueries({ queryKey: ['personas'] });
      void queryClient.invalidateQueries({ queryKey: ['content'] });
      void navigate({ to: '/personas' });
    },
    onError: (err) => {
      toast.error('Delete failed', { description: String(err) });
    },
  });

  const restoreM = useMutation({
    mutationFn: () => api.restorePersona(personaId),
    onSuccess: () => {
      toast.success('Persona restored', { description: 'Moved back out of Trash.' });
      setRestoreOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['persona', personaId] });
      void queryClient.invalidateQueries({ queryKey: ['personas'] });
      void queryClient.invalidateQueries({ queryKey: ['content'] });
    },
    onError: (err) => toast.error('Restore failed', { description: String(err) }),
  });

  const purgeM = useMutation({
    mutationFn: () => api.deletePersona(personaId, { purge: true, cascade: true }),
    onSuccess: () => {
      toast.success('Persona permanently deleted');
      void queryClient.invalidateQueries({ queryKey: ['personas'] });
      void queryClient.invalidateQueries({ queryKey: ['content'] });
      void navigate({ to: '/personas' });
    },
    onError: (err) => toast.error('Delete failed', { description: String(err) }),
  });

  if (personaQ.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (personaQ.isError || !personaQ.data) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm">
        Persona not found.
      </div>
    );
  }

  const p = personaQ.data;
  const hero = p.avatar_references?.image_urls?.[0];
  const isTrashed = !!p.deleted_at;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-4">
          <Avatar src={hero ?? null} alt={p.voice_sheet?.display_name ?? ''} size="xl" />
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              {p.voice_sheet?.display_name ?? '—'}
            </h1>
            <p className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
              {p.persona_id}
              <CopyButton value={p.persona_id} label={`Copy ${p.persona_id}`} />
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Badge>{archetypeLabel(p.archetype)}</Badge>
              <Badge tone="neutral">{p.target_platform}</Badge>
              <Badge tone="neutral">{p.language}</Badge>
              <span className="text-xs text-muted-foreground">created {formatRelative(p.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isTrashed ? (
            <>
              <Button
                variant="outline"
                onClick={() => setRestoreOpen(true)}
                disabled={restoreM.isPending || purgeM.isPending}
                title="Restore persona out of Trash"
              >
                <RotateCcw className="h-4 w-4" />
                {restoreM.isPending ? 'Restoring…' : 'Restore'}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setPurgeOpen(true)}
                disabled={restoreM.isPending || purgeM.isPending}
                title="Permanently delete persona + linked content"
              >
                <Trash2 className="h-4 w-4" />
                {purgeM.isPending ? 'Deleting…' : 'Delete forever'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setDeleteOpen(true)}
                disabled={deleteM.isPending}
                title="Move persona to Trash"
              >
                <Trash2 className="h-4 w-4" />
                {deleteM.isPending ? 'Deleting…' : 'Delete'}
              </Button>
              <Link to="/triggers/content" search={{ persona_id: p.persona_id }}>
                <Button>Generate content</Button>
              </Link>
            </>
          )}
        </div>
      </header>

      <Tabs<TabKey>
        tabs={[
          { value: 'voice', label: 'Voice sheet' },
          { value: 'brief', label: 'Brief' },
          { value: 'gallery', label: 'Gallery', count: p.avatar_references?.image_urls?.length ?? 0 },
          { value: 'content', label: 'Linked content' },
          { value: 'accounts', label: 'Linked accounts' },
          { value: 'research', label: 'Research' },
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === 'research' && <ResearchPanel personaId={p.persona_id} />}

      {tab === 'brief' && <PersonaBriefPanel personaId={p.persona_id} />}

      {tab === 'voice' && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FieldCard label="Bio" body={p.voice_sheet?.bio} />
          <FieldCard label="Audience" body={p.voice_sheet?.audience || null} />
          <FieldCard label="Physical appearance" body={p.voice_sheet?.physical_appearance} />
          {p.voice_sheet?.visual_identity && (
            <FieldCard
              label="Visual identity"
              body={[
                p.voice_sheet.visual_identity.style,
                p.voice_sheet.visual_identity.palette?.length
                  ? `Palette: ${p.voice_sheet.visual_identity.palette.join(', ')}`
                  : '',
                p.voice_sheet.visual_identity.wardrobe
                  ? `Wardrobe: ${p.voice_sheet.visual_identity.wardrobe}`
                  : '',
                p.voice_sheet.visual_identity.setting
                  ? `Setting: ${p.voice_sheet.visual_identity.setting}`
                  : '',
              ]
                .filter(Boolean)
                .join('\n')}
            />
          )}
          {(p.voice_sheet?.pillars?.length ?? 0) > 0 && (
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Content pillars</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {(p.voice_sheet?.pillars ?? []).map((pillar, i) => (
                    <li
                      key={pillar.name}
                      className="rounded-md border border-border bg-muted/30 px-3 py-2"
                    >
                      <p className="text-sm font-medium">
                        <span className="mr-1.5 text-muted-foreground">{i + 1}.</span>
                        {pillar.name}
                      </p>
                      {pillar.description && (
                        <p className="mt-1 text-xs text-muted-foreground">{pillar.description}</p>
                      )}
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}
          <ListCard label="Tone" items={p.voice_sheet?.tone} />
          <ListCard label="Speech quirks" items={p.voice_sheet?.speech_quirks} />
          <ListCard label="Topics" items={p.voice_sheet?.topics} />
          <ListCard label="Do" items={p.voice_sheet?.dos} />
          <ListCard label="Don't" items={p.voice_sheet?.donts} />
          {(p.voice_sheet?.claims_boundaries?.length ?? 0) > 0 && (
            <ListCard label="Claims boundaries" items={p.voice_sheet?.claims_boundaries ?? null} />
          )}
          {p.seed_traits && Object.keys(p.seed_traits).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Seed traits</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="overflow-x-auto rounded-md bg-muted p-2 text-xs">
                  {JSON.stringify(p.seed_traits, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {tab === 'gallery' && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {(p.avatar_references?.image_urls ?? []).map((url, i) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              className="group relative block aspect-square overflow-hidden rounded-lg border border-border bg-card"
            >
              <img src={url} alt={`avatar ${i}`} className="h-full w-full object-cover" />
              <span className="absolute bottom-2 left-2 rounded bg-background/80 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-foreground">
                {i === 0 ? 'hero' : `var ${i}`}
              </span>
            </a>
          ))}
        </div>
      )}

      {tab === 'content' && (
        <div>
          {contentQ.isLoading ? (
            <Skeleton className="h-32" />
          ) : (contentQ.data ?? []).length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No content generated for this persona yet.
              </CardContent>
            </Card>
          ) : (
            <ul className="space-y-2">
              {(contentQ.data ?? []).map((c) => (
                <Link
                  key={c.content_id}
                  to="/content/$contentId"
                  params={{ contentId: c.content_id }}
                  className="block"
                >
                  <Card className="transition-colors hover:border-foreground/30">
                    <CardContent className="flex items-center justify-between py-3">
                      <div className="space-y-0.5">
                        <p className="text-sm font-medium">
                          {c.script?.caption?.slice(0, 80) ?? '—'}
                        </p>
                        <p className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
                          {shortId(c.content_id)}
                          <CopyButton value={c.content_id} label={`Copy ${c.content_id}`} />
                        </p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{c.video?.provider_model}</span>
                        <span>{formatRelative(c.created_at)}</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === 'accounts' && (
        <AccountsPanel personaId={personaId} accounts={accountsQ.data ?? []} loading={accountsQ.isLoading} />
      )}

      {deleteOpen && (
        <ConfirmDialog
          title={`Move "${p.voice_sheet?.display_name ?? personaId}" to Trash?`}
          description="Its linked content moves to Trash too. You can restore everything later from the Trash view on the Personas list."
          confirmLabel="Move to Trash"
          destructive
          pending={deleteM.isPending}
          onConfirm={() => deleteM.mutate()}
          onCancel={() => setDeleteOpen(false)}
        />
      )}

      {restoreOpen && (
        <ConfirmDialog
          title={`Restore "${p.voice_sheet?.display_name ?? personaId}"?`}
          description="The persona and its linked content move back out of Trash."
          confirmLabel="Restore"
          pending={restoreM.isPending}
          onConfirm={() => restoreM.mutate()}
          onCancel={() => setRestoreOpen(false)}
        />
      )}

      {purgeOpen && (
        <ConfirmDialog
          title="Delete forever?"
          description="Permanently delete this persona and all its linked content + video files. This cannot be undone."
          confirmLabel="Delete forever"
          destructive
          pending={purgeM.isPending}
          onConfirm={() => purgeM.mutate()}
          onCancel={() => setPurgeOpen(false)}
        />
      )}
    </div>
  );
}
